import React, { useMemo, useEffect, useState } from 'react';
import VoucherEngine from '../components/VoucherEngine';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { AccountingEngine } from '../services/accountingEngine';
import { useSystemDate } from '../hooks/useSystemDate';

export const VoucherEngineWrapper = ({ handleItemSelect, passedEntityId, activeMenuLevel }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const systemDate = useSystemDate();

  // Read voucherId and isDuplicateMode from store — set synchronously before navigation,
  // so there is zero timing gap between navStack update and prop delivery.
  const storedVoucherId = useStore((state) => state.currentAlterVoucherId);
  const voucherId = passedEntityId || storedVoucherId;
  const storeIsDuplicateMode = useStore((state) => state.currentIsDuplicateMode);
  const isDuplicateMode = (activeMenuLevel === 'duplicate_voucher_form') || storeIsDuplicateMode;

  const isLoadMode = !!(voucherId);
  const isAlterMode = isLoadMode && !isDuplicateMode;
  const currentVoucherType = useStore((state) => state.currentVoucherType) || 'Sales';

  // ── Master data ──────────────────────────────────────────────────────────────
  const ledgers    = useLiveQuery(() => activeCompany ? db.ledgers.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const groups     = useLiveQuery(() => activeCompany ? db.groups.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const stockItems = useLiveQuery(() => activeCompany ? db.stockItems.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbVoucherTypes = useLiveQuery(() => activeCompany ? db.voucherTypes.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const baseCurrency   = useLiveQuery(() => activeCompany ? db.currencies.where('companyId').equals(activeCompany.id!).toArray().then(arr => arr.find(c => c.isBaseCurrency)) : null, [activeCompany]);
  const nextVoucherNumber = useLiveQuery(() => activeCompany ? AccountingEngine.getNextVoucherNumber(activeCompany.id!, currentVoucherType) : '1', [activeCompany, currentVoucherType]);

  // ── Alter mode: manually load voucher data (not via useLiveQuery) ─────────────
  // Using useState + useEffect instead of useLiveQuery eliminates the
  // null/undefined ambiguity that caused the "Creation mode flash" bug.
  // States:
  //   'loading'  → fetch in progress
  //   'found'    → voucher loaded successfully
  //   'notfound' → voucher ID not in DB
  const [voucherLoadState, setVoucherLoadState] = useState<'loading' | 'found' | 'notfound'>('loading');
  const [rawVoucherData, setRawVoucherData] = useState<{ voucher: any; entries: any[]; invEntries: any[] } | null>(null);

  useEffect(() => {
    if (!isLoadMode || !voucherId) return;
    // Reset on every voucherId change
    setVoucherLoadState('loading');
    setRawVoucherData(null);

    const load = async () => {
      const vId = Number(voucherId);
      if (isNaN(vId)) { setVoucherLoadState('notfound'); return; }
      const voucher = await db.vouchers.get(vId);
      if (!voucher) { setVoucherLoadState('notfound'); return; }
      const entries    = await db.voucherEntries.where('voucherId').equals(vId).toArray();
      const invEntries = await db.inventoryEntries.where('voucherId').equals(vId).toArray();
      setRawVoucherData({ voucher, entries, invEntries });
      setVoucherLoadState('found');
    };

    load();
  }, [voucherId, isLoadMode]);

  // ── Live ledger balances (for right-panel display) ──────────────────────────
  const liveBalances = useLiveQuery(async () => {
    if (!activeCompany || !ledgers || ledgers.length === 0) return null;
    try {
      return await AccountingEngine.getCompanyBalancesFast(activeCompany.id!);
    } catch { return null; }
  }, [activeCompany, ledgers]);

  // ── erpLedgers map ───────────────────────────────────────────────────────────
  const erpLedgers = useMemo(() => {
    if (!ledgers || !groups) return null;
    const groupMap = new Map(groups.map(g => [g.id, g.name]));
    const result: Record<string, any> = {};
    ledgers.forEach(l => {
      const groupName = l.groupId ? groupMap.get(l.groupId) : 'Primary';
      // Use live running balance if available, otherwise fall back to opening balance
      const liveBal = liveBalances?.get(l.id!);
      const balStr = liveBal
        ? `${liveBal.balance.toFixed(2)} ${liveBal.type}`
        : `${Math.abs(l.openingBalance).toFixed(2)} ${l.balanceType}`;
      result[l.name] = {
        group: groupName || 'Primary',
        balance: balStr,
        taxRate:     l.taxRate     || 0,
        gstin:       l.gstin       || '',
        state:       l.state       || '',
        billByBill:  (l as any).billByBill  || 'No',
        creditLimit: (l as any).creditLimit || 0,
        ledgerId:    l.id!,
      };
    });
    return result;
  }, [ledgers, groups, liveBalances]);

  // ── erpItems map ─────────────────────────────────────────────────────────────
  // Live stock calculation — opening + inward - outward
  const liveStock = useLiveQuery(async () => {
    if (!activeCompany || !stockItems || !dbVoucherTypes) return new Map<number, number>();
    const vouchers = await db.vouchers.where('companyId').equals(activeCompany.id!).filter(v => !v.deleted).toArray();
    const voucherIds = vouchers.map(v => v.id!);
    const invEntries = voucherIds.length > 0 ? await db.inventoryEntries.where('voucherId').anyOf(voucherIds).toArray() : [];

    const vtDirMap = new Map<string, 'In' | 'Out'>();
    dbVoucherTypes.forEach((vt: any) => { if (vt.affectsStock && vt.stockDirection) vtDirMap.set(vt.name, vt.stockDirection); });
    const getDir = (vType: string): 'In' | 'Out' | null => {
      if (vtDirMap.has(vType)) return vtDirMap.get(vType)!;
      if (['Purchase', 'Receipt Note', 'Debit Note'].includes(vType)) return 'In';
      if (['Sales', 'Delivery Note', 'Credit Note'].includes(vType)) return 'Out';
      return null;
    };

    const stockMap = new Map<number, number>();
    stockItems.forEach(si => stockMap.set(si.id!, si.openingBalance || 0));

    invEntries.forEach(ie => {
      const v = vouchers.find(v => v.id === ie.voucherId);
      if (!v) return;
      const dir = getDir(v.type);
      const current = stockMap.get(ie.stockItemId) || 0;
      if (dir === 'In') stockMap.set(ie.stockItemId, current + ie.quantity);
      else if (dir === 'Out') stockMap.set(ie.stockItemId, current - ie.quantity);
    });

    return stockMap;
  }, [activeCompany, stockItems, dbVoucherTypes]);

  const erpItems = useMemo(() => {
    if (!stockItems) return null;
    const result: Record<string, any> = {};
    stockItems.forEach(item => {
      const currentStock = liveStock?.get(item.id!) ?? item.openingBalance ?? 0;
      const unit = 'Nos';
      result[item.name] = { rate: item.openingRate || 0, stock: `${currentStock} ${unit}`, id: item.id, gstRate: item.gstRate ?? 18, hsn: item.hsn || '', mrp: (item as any).mrp || 0, pack: (item as any).pack || '', saltComposition: (item as any).saltComposition || '', manufacturer: (item as any).manufacturer || '' };
    });
    return result;
  }, [stockItems, liveStock]);

  // ── Build pre-filled form from raw DB data ───────────────────────────────────
  const initialVoucherData = useMemo(() => {
    if (!isLoadMode || !rawVoucherData || !ledgers || !groups || !dbVoucherTypes) return null;
    const { voucher, entries, invEntries } = rawVoucherData;
    const vType = voucher.type;
    
    // Derive mode dynamically from voucher type definitions to support custom types
    const vtDef = dbVoucherTypes.find((vt: any) => vt.name === vType);
    const isDefaultSales = vType === 'Sales' || vType === 'Credit Note';
    const isDefaultPurchase = vType === 'Purchase' || vType === 'Debit Note';
    const affectsStock = vtDef ? vtDef.affectsStock : (isDefaultSales || isDefaultPurchase);
    
    const isSalesType   = isDefaultSales || (vtDef && vtDef.name.toLowerCase().includes('sales'));
    const isPurchaseType = isDefaultPurchase || (vtDef && vtDef.name.toLowerCase().includes('purchase'));
    
    // If it affects stock OR is explicitly an invoice type, we map as invoice. Otherwise map as journal.
    const isInvoiceType = affectsStock || isSalesType || isPurchaseType;
    const ledgerMap = new Map(ledgers.map(l => [l.id, l]));

    let formData: any = {
      party:       { text: '', value: '' },
      sales:       { text: '', value: '' },
      purchase:    { text: '', value: '' },
      journalRows: [],
      items:       [{ name: { text: '', value: '' }, qty: '', rate: '', disc: '', amount: '' }],
      charges:     [{ ledger: { text: '', value: '' }, amount: '' }],
      narration:   voucher.narration || '',
    };

    if (!isInvoiceType) {
      formData.journalRows = entries.map((e: any) => {
        const led = ledgerMap.get(e.ledgerId);
        const name = led?.name || '';
        return { drCr: e.type, ledger: { text: name, value: name }, debit: e.type === 'Dr' ? String(e.amount) : '', credit: e.type === 'Cr' ? String(e.amount) : '' };
      });
      if (formData.journalRows.length === 0)
        formData.journalRows = [{ drCr: 'Dr', ledger: { text: '', value: '' }, debit: '', credit: '' }];

    } else {
      const groupMap = new Map(groups.map((g: any) => [g.id, g.name]));
      const CHARGE_GROUPS = new Set(['Duties & Taxes', 'Direct Expenses', 'Indirect Expenses', 'Direct Income', 'Indirect Income']);
      const isChargeEntry = (e: any) => {
        const led = ledgerMap.get(e.ledgerId);
        if (!led) return false;
        const gName = led.groupId ? groupMap.get(led.groupId) : '';
        return gName ? CHARGE_GROUPS.has(gName) : false;
      };

      const partyDrCr = isSalesType ? 'Dr' : 'Cr';
      const partyEntry = entries.find((e: any) => e.type === partyDrCr && !isChargeEntry(e)) || entries.find((e: any) => e.type === partyDrCr);
      if (partyEntry) {
        const led = ledgerMap.get(partyEntry.ledgerId);
        formData.party = { text: led?.name || '', value: led?.name || '' };
      }

      const mainDrCr = isSalesType ? 'Cr' : 'Dr';
      const mainEntry = entries.find((e: any) => e.type === mainDrCr && e.ledgerId !== partyEntry?.ledgerId && !isChargeEntry(e))
                     || entries.find((e: any) => e.type === mainDrCr && e.ledgerId !== partyEntry?.ledgerId);
      if (mainEntry) {
        const led = ledgerMap.get(mainEntry.ledgerId);
        if (isSalesType) formData.sales    = { text: led?.name || '', value: led?.name || '' };
        else             formData.purchase = { text: led?.name || '', value: led?.name || '' };
      }

      const usedIds = new Set([partyEntry?.ledgerId, mainEntry?.ledgerId]);
      const chargeEntries = entries.filter((e: any) => !usedIds.has(e.ledgerId));
      if (chargeEntries.length > 0) {
        formData.charges = chargeEntries.map((e: any) => {
          const led = ledgerMap.get(e.ledgerId);
          return { ledger: { text: led?.name || '', value: led?.name || '' }, amount: String(e.amount) };
        });
        if (formData.charges[formData.charges.length - 1].ledger.value)
          formData.charges.push({ ledger: { text: '', value: '' }, amount: '' });
      }

      if (invEntries.length > 0) {
        formData.items = invEntries.map((ie: any) => {
          const si = stockItems?.find((s: any) => s.id === ie.stockItemId);
          return { name: { text: si?.name || '', value: si?.name || '' }, qty: String(ie.quantity), rate: String(ie.rate), disc: ie.discPercent ? String(ie.discPercent) : '', amount: String(ie.amount), batch: ie.batchNumber || '', expiry: ie.expiryDate || '', mrp: ie.mrp ? String(ie.mrp) : '' };
        });
        if (formData.items[formData.items.length - 1].name.value)
          formData.items.push({ name: { text: '', value: '' }, qty: '', rate: '', disc: '', amount: '', batch: '', expiry: '', mrp: '' });
      }
    }

    return {
      voucherType:   vType,
      referenceNumber: isDuplicateMode ? '' : voucher.referenceNumber,
      voucherNumber: voucher.voucherNumber,
      date:          isDuplicateMode ? systemDate : voucher.date,
      formData,
    };
  }, [isLoadMode, isDuplicateMode, nextVoucherNumber, systemDate, rawVoucherData, ledgers, stockItems, groups, dbVoucherTypes]);

  // ── Save handler ─────────────────────────────────────────────────────────────
  const handleSaveVoucher = async (voucherData: any) => {
    if (!activeCompany) throw new Error('No active company');
    const { voucherType, referenceNumber, date, narration, entries, items, billAllocations } = voucherData;

    const formattedEntries = entries.map((e: any) => {
      const ledger = ledgers?.find(l => l.name === e.ledger);
      if (!ledger) throw new Error(`Ledger "${e.ledger}" not found`);
      return { type: e.type, ledgerId: ledger.id!, amount: Number(String(e.amount).replace(/,/g, '')) };
    });

    const validInvEntries: any[] = [];
    if (items && items.length > 0) {
      items.forEach((item: any) => {
        if (item.name.value && item.qty) {
          const si = stockItems?.find(s => s.name === item.name.value);
          if (!si) throw new Error(`Stock Item "${item.name.value}" not found`);
          const invEntry: any = {
            stockItemId: si.id!,
            locationId:  0,
            quantity:    Number(String(item.qty).replace(/,/g, '')),
            rate:        Number(String(item.rate).replace(/,/g, '')),
            amount:      Number(String(item.amount).replace(/,/g, '')),
          };
          if (item.disc) invEntry.discPercent = Number(String(item.disc).replace(/,/g, ''));
          if (item.batch) invEntry.batchNumber = item.batch;
          if (item.expiry) invEntry.expiryDate = item.expiry;
          if (item.mrp) invEntry.mrp = Number(String(item.mrp).replace(/,/g, ''));
          validInvEntries.push(invEntry);
        }
      });
    }

    try {
      if (isAlterMode && voucherId) {
        await AccountingEngine.updateVoucher(
          voucherId,
          { companyId: activeCompany.id!, type: voucherType, date: date || systemDate, referenceNumber: referenceNumber || '', narration: narration || '' },
          formattedEntries,
          validInvEntries,
          billAllocations
        );
      } else {
        await AccountingEngine.postVoucher(
          { companyId: activeCompany.id!, type: voucherType, date: date || systemDate, referenceNumber: referenceNumber || '', narration: narration || '' },
          formattedEntries,
          validInvEntries,
          billAllocations
        );
      }
    } catch (err: any) {
      alert(`Voucher Save Error:\n\n${err.message}`);
      throw err;
    }
  };

  const handleLedgerCreated = async (newItem: any) => {
    if (!activeCompany) return;
    const exists = await db.ledgers.where('[companyId+name]').equals([activeCompany.id!, String(newItem.name).trim()]).first();
    if (exists) { alert('Ledger already exists'); return; }
    const group = groups?.find(g => g.name === newItem.group);
    await db.ledgers.add({ companyId: activeCompany.id!, name: newItem.name, alias: newItem.alias, groupId: group?.id || null, openingBalance: newItem.openingBalance || 0, balanceType: newItem.balanceType || 'Dr', taxRate: newItem.taxRate || 0, isDefault: false });
  };

  const handleItemCreated = async (newItem: any) => {
    if (!activeCompany) return;
    const exists = await db.stockItems.where('[companyId+name]').equals([activeCompany.id!, String(newItem.name).trim()]).first();
    if (exists) { alert('Stock Item already exists'); return; }
    let unit = await db.units.where({ companyId: activeCompany.id!, symbol: newItem.unit }).first();
    if (!unit && newItem.unit) {
      const unitId = await db.units.add({ companyId: activeCompany.id!, symbol: newItem.unit, formalName: newItem.unit, decimalPlaces: 0 });
      unit = { id: unitId as number, symbol: newItem.unit, companyId: activeCompany.id!, formalName: newItem.unit, decimalPlaces: 0 };
    }
    await db.stockItems.add({ companyId: activeCompany.id!, name: newItem.name, stockGroupId: null, stockCategoryId: null, unitId: unit?.id || 1, openingBalance: newItem.openingBalance || 0, openingRate: newItem.openingRate || 0, openingValue: newItem.openingValue || 0 });
  };

  // ── Loading guards — clear and unambiguous ───────────────────────────────────
  // 1. Masters not ready yet
  if (!erpLedgers || !erpItems) return <div className="p-4 text-gray-500">Loading masters...</div>;

  // 2. Alter/Duplicate mode — voucher fetch in progress
  if (isLoadMode && voucherLoadState === 'loading') return <div className="p-4 text-gray-500">Loading voucher...</div>;

  // 3. Alter/Duplicate mode — voucher not found in DB
  if (isLoadMode && voucherLoadState === 'notfound') return (
    <div className="p-4 text-red-500 font-bold">
      Voucher not found (ID: {voucherId}).{' '}
      <button className="underline" onClick={() => handleItemSelect({ name: 'Quit' })}>Back</button>
    </div>
  );

  // 4. Load mode — voucher found but form data mapping not ready yet
  if (isLoadMode && !initialVoucherData) return <div className="p-4 text-gray-500">Preparing voucher...</div>;

  return (
    <div className="absolute inset-0 z-50 bg-white">
      <VoucherEngine
        key={isLoadMode ? `load-${voucherId}-${isDuplicateMode}` : `new-${activeCompany?.id || 'entry'}`}
        erpLedgers={erpLedgers}
        erpItems={erpItems}
        erpVoucherTypes={dbVoucherTypes}
        baseCurrency={baseCurrency}
        onSaveVoucher={handleSaveVoucher}
        onQuit={() => handleItemSelect({ name: 'Quit' })}
        onLedgerCreated={handleLedgerCreated}
        onItemCreated={handleItemCreated}
        erpVoucherNumber={isLoadMode ? (initialVoucherData?.voucherNumber || '') : (nextVoucherNumber || '1')}
        initialDate={isLoadMode ? (initialVoucherData?.date || systemDate) : systemDate}
        activeCompanyName={activeCompany?.name}
        initialVoucherData={isLoadMode ? initialVoucherData : null}
        isAlterMode={isAlterMode}
        isDuplicateMode={isDuplicateMode}
        voucherId={isLoadMode ? voucherId : null}
      />
    </div>
  );
};
